export const LOCALES = ["ru", "en"] as const;
export type Locale = (typeof LOCALES)[number];

export const DEFAULT_LOCALE: Locale = "ru";

export type Dictionary = Readonly<Record<string, string>>;
export type Dictionaries = Readonly<Record<Locale, Dictionary>>;

export interface LocaleFile {
  ru: Dictionary;
  en: Dictionary;
}

export function isLocale(value: unknown): value is Locale {
  return (
    typeof value === "string" && (LOCALES as readonly string[]).includes(value)
  );
}

export function defineLocale<const K extends string>(file: {
  ru: Readonly<Record<K, string>>;
  en: Readonly<Record<K, string>>;
}): LocaleFile {
  return file;
}

function isDictionary(value: unknown): value is Dictionary {
  if (typeof value !== "object" || value === null) return false;
  return Object.values(value).every((entry) => typeof entry === "string");
}

function isLocaleFile(value: unknown): value is LocaleFile {
  if (typeof value !== "object" || value === null) return false;
  return (
    "ru" in value &&
    "en" in value &&
    isDictionary(value.ru) &&
    isDictionary(value.en)
  );
}

export function findLocaleProblems(file: LocaleFile): string[] {
  const problems: string[] = [];
  for (const [from, to] of [
    ["ru", "en"],
    ["en", "ru"],
  ] as const) {
    for (const key of Object.keys(file[from])) {
      if (!(key in file[to])) problems.push(`missing in ${to}: ${key}`);
    }
  }
  for (const locale of LOCALES) {
    for (const [key, value] of Object.entries(file[locale])) {
      if (value.trim() === "") problems.push(`empty in ${locale}: ${key}`);
    }
  }
  return problems;
}

export function mergeDictionaries(files: readonly LocaleFile[]): Dictionaries {
  const ru: Record<string, string> = {};
  const en: Record<string, string> = {};
  for (const file of files) {
    Object.assign(ru, file.ru);
    Object.assign(en, file.en);
  }
  return { ru, en };
}

const modules = import.meta.glob<unknown>("./locales/picks/*.ts", {
  eager: true,
  import: "default",
});

export const localeFiles: Readonly<Record<string, LocaleFile>> =
  Object.fromEntries(
    Object.entries(modules).filter((entry): entry is [string, LocaleFile] =>
      isLocaleFile(entry[1]),
    ),
  );

export const dictionaries: Dictionaries = mergeDictionaries(
  Object.values(localeFiles),
);

export type TranslateParams = Readonly<Record<string, string | number>>;

export const MISSING_MARKER = "[missing translation]";
const PLACEHOLDER = /\{(\w+)\}/g;

function interpolate(
  template: string,
  params: TranslateParams | undefined,
): string {
  if (!params) return template;
  return template.replace(PLACEHOLDER, (match, name: string) => {
    const value = params[name];
    return value === undefined ? match : String(value);
  });
}

export function translate(
  messages: Dictionaries,
  locale: Locale,
  key: string,
  params?: TranslateParams,
): string {
  const other: Locale = locale === "ru" ? "en" : "ru";
  const template = messages[locale][key] ?? messages[other][key];
  if (template === undefined) return MISSING_MARKER;
  return interpolate(template, params);
}
